import { Injectable } from '@angular/core';
import { KeycloakService } from 'keycloak-angular';
import { ILogMessage, LogLevel } from '../../../model/log';
import { EnvService } from '../configuration/env.service';
import { LocationContextService } from '../location-context/location-context.service';

@Injectable({
    providedIn: 'root'
})
export class LogService {
    configLevel: LogLevel = LogLevel.Info;
    configLogToConsole: boolean;

    // We have to inject the Keycloak service here instead of the authentication service because it creates a circular dependency
    constructor(
        private readonly keycloakService: KeycloakService,
        private readonly locationContextService: LocationContextService,
        private readonly env: EnvService
    ) {
        this.configLevel = this.env.config.log.logLevel;
        this.configLogToConsole = this.env.config.log.logToConsole;
    }

    private shouldLog(level: LogLevel) {
        if (this.configLevel === LogLevel.None) {
            return false;
        } else if (this.configLevel === LogLevel.Error) {
            return level === LogLevel.Error;
        } else if (this.configLevel === LogLevel.Warn) {
            return level === LogLevel.Error || level === LogLevel.Warn;
        } else if (this.configLevel === LogLevel.Info) {
            return level === LogLevel.Error || level === LogLevel.Warn || level === LogLevel.Info;
        } else {
            return true;
        }
    }

    private appendContextData(logMessage: ILogMessage): ILogMessage {
        // Add user data
        try {
            const user = this.keycloakService.getUsername();
            logMessage.data = { ...logMessage.data, user };
        } catch (e) {
            logMessage.data = { ...logMessage.data, user: '' };
        }

        // Add location context data
        try {
            const locationContext = this.locationContextService.getLocationContext();
            logMessage.data = { ...logMessage.data, locationContext };
        } catch (e) {
            logMessage.data = { ...logMessage.data, locationContext: '' };
        }

        return logMessage;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private logToConsole(logMessage: ILogMessage) {
        if (this.configLogToConsole) {
            const messageFormatted = `[${logMessage.source}] ${logMessage.message}`;
            switch (logMessage.level) {
                case LogLevel.Error:
                    // eslint-disable-next-line no-console
                    console.error(messageFormatted, logMessage.data);
                    break;
                case LogLevel.Warn:
                    // eslint-disable-next-line no-console
                    console.warn(messageFormatted, logMessage.data);
                    break;
                case LogLevel.Info:
                    // eslint-disable-next-line no-console
                    console.info(messageFormatted, logMessage.data);
                    break;
                default:
                    // eslint-disable-next-line no-console
                    console.debug(messageFormatted, logMessage.data);
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private log(source: string, message: string, level: LogLevel, data: any) {
        if (!source) {
            throw new Error('Source is empty');
        }
        if (!message) {
            throw new Error('Message is empty');
        }

        if (this.shouldLog(level)) {
            let logMessage: ILogMessage = {
                source,
                message,
                level,
                data
            };
            logMessage = this.appendContextData(logMessage);
            this.logToConsole(logMessage);
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error(source: string, message: string, data: any) {
        this.log(source, message, LogLevel.Error, data);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    warn(source: string, message: string, data: any) {
        this.log(source, message, LogLevel.Warn, data);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    info(source: string, message: string, data: any) {
        this.log(source, message, LogLevel.Info, data);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    verbose(source: string, message: string, data: any) {
        this.log(source, message, LogLevel.Verbose, data);
    }
}
